import {
	CSSProperties,
	ReactNode,
	RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';

import { usePremadeQuizzes } from '@quizzach/api-hooks';
import {
	IcArrowRight,
	IcBasket,
	IcCheck,
	IcClose,
	IcDoc,
} from '@quizzach/assets';
import {
	AlertMessage,
	AuthorMark,
	CardAuthorMark,
	Modal,
	ModalShell,
	Navigation,
	useGiftBasket,
} from '@quizzach/components';
import { useWindowListener } from '@quizzach/contexts';
import { ApiFailedReason, IPremadeQuiz, RoutePath } from '@quizzach/interfaces';
import {
	BtnLine,
	BtnTeal,
	KChip,
	FaqQRow,
	ModalCloseBtn,
	QuickAddBtn,
	SaleCard,
	Sk,
	SpecBar,
	VsBand,
	VsCover,
	VsEndCard,
	VsFaq,
	VsFilterChips,
	VsFilterOpen,
	VsGrid,
	VsHero,
	VsHeroCard,
	VsNone,
	VsPage,
	VsPool,
	VsQuizPanel,
	VsRail,
	VsSection,
	VsShelfHead,
	VsThemes,
	VsTopGrid,
} from '@quizzach/styles';
import { Reveal, useParallax } from '@quizzach/motion';
import { Confetti, GiftCardStack, seeded } from './GiftCards';
import { GiftTabKey, GiftTabs } from './GiftTabs';
import {
	GiftLine,
	QUICK_GIFT_OPTIONS,
	QUICK_GIFT_PRICES,
	handleDownloadPremadeTemplate,
	premadeLevelKey,
	sortBySectionOrder,
	sortQuizzesNewestFirst,
} from '@quizzach/utils';

const PHONE = 768;
const FAQ_COUNT = 8;
const RAIL_SIZE = 10;
const FEATURED_SIZE = 6;
// where the tabs stick under the nav bar; matches VsTabs in the styles

type ShelfKey =
	| 'top'
	| 'film'
	| 'sport'
	| 'put'
	| 'glazba'
	| 'poveznica'
	| 'ostalo'
	| 'djeca'
	| 'spec'
	| 'sve';
const NAV_BAR_PX = 74;
const SHELL_BREAK_PX = 1079;
const SECTION_GAP_PX = 24;
type Filters = { levels: number[]; authors: string[] };

const NO_FILTERS: Filters = { levels: [], authors: [] };

const THEME_TAGS: Partial<Record<ShelfKey, string>> = {
	film: 'film&tv',
	sport: 'sport',
	put: 'put oko svijeta',
	glazba: 'glazba',
	poveznica: 'poveznica',
};

const stroke = (paths: ReactNode) => (
	<svg
		viewBox='0 0 24 24'
		width='1em'
		height='1em'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.9'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		{paths}
	</svg>
);

const THEME_TILES: { key: ShelfKey; icon: ReactNode }[] = [
	{
		key: 'top',
		icon: stroke(
			<path d='M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z' />
		),
	},
	{
		key: 'film',
		icon: stroke(
			<>
				<rect x='3' y='4' width='18' height='16' rx='2' />
				<path d='M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4' />
			</>
		),
	},
	{
		key: 'sport',
		icon: stroke(
			<>
				<circle cx='12' cy='12' r='9' />
				<path d='M12 7.5l4 2.9-1.5 4.6h-5L8 10.4z' />
				<path d='M12 3v4.5M16 10.4l4.4-1.4M14.5 15l2.7 3.8M9.5 15l-2.7 3.8M8 10.4L3.6 9' />
			</>
		),
	},
	{
		key: 'put',
		icon: stroke(
			<>
				<path d='M21 3L10 14' />
				<path d='M21 3l-7 18-4-7-7-4z' />
			</>
		),
	},
	{
		key: 'glazba',
		icon: stroke(
			<>
				<path d='M9 18V5l11-2v13' />
				<circle cx='6' cy='18' r='3' />
				<circle cx='17' cy='16' r='3' />
			</>
		),
	},
	{
		key: 'poveznica',
		icon: stroke(
			<>
				<path d='M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1' />
				<path d='M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1' />
			</>
		),
	},
	{
		key: 'ostalo',
		icon: stroke(<path d='M7 4c3-2 9-1 9 4 0 3-3 4-4 6M11 19h.01' />),
	},
	{
		key: 'djeca',
		icon: stroke(
			<>
				<circle cx='12' cy='12' r='9' />
				<path d='M8.5 14.5c.9 1.3 2.1 2 3.5 2s2.6-.7 3.5-2' />
				<path d='M9 9.5h.01M15 9.5h.01' />
			</>
		),
	},
];

const SlidersIcon = () =>
	stroke(
		<>
			<path d='M4 7h10M18 7h2M4 17h2M10 17h10' />
			<circle cx='16' cy='7' r='2' />
			<circle cx='8' cy='17' r='2' />
		</>
	);

const assetUrl = (file?: string) =>
	`${import.meta.env.VITE_ASSETS_URL}/thematic_quizzes/${file}`;

const cardPrice = (price?: string) =>
	price?.replace('.00', '').replace('.', ',');

const LEVEL_TONES: Record<number, 'teal' | 'gold' | 'coral'> = {
	1: 'teal',
	2: 'gold',
	3: 'coral',
};

const hasTag = (quiz: IPremadeQuiz, tag: string) =>
	(quiz.primary_thematic_tags ?? []).some((t) => t.name === tag);

const Hero = ({
	onGift,
	onRedeem,
	phone,
}: {
	onGift: () => void;
	onRedeem: () => void;
	phone: boolean;
}) => {
	const { t } = useTranslation();
	const cardsRef = useRef<HTMLDivElement>(null);

	// the cards drift with the scroll like the homepage hero photo (gentle)
	useParallax(cardsRef, 0.06);

	return (
		<VsHero>
			<div className='art'>
				<Confetti
					count={phone ? 26 : 54}
					logos={phone ? 8 : 18}
					seed={11}
					burst
				/>
				<Reveal as='h1'>
					{t('voucherShop.hero.title')}
					<em>{t('voucherShop.hero.titleAccent')}</em>
				</Reveal>
				<Reveal delay={1}>
					<GiftCardStack phone={phone} cardsRef={cardsRef} />
				</Reveal>
			</div>
			<VsHeroCard>
				<Reveal as='p'>{t('voucherShop.hero.text')}</Reveal>
				<Reveal className='btns' delay={1}>
					<BtnTeal type='button' onClick={onGift}>
						{t('voucherShop.hero.gift')}
						<IcArrowRight size={18} />
					</BtnTeal>
					<BtnLine type='button' onClick={onRedeem}>
						{t('voucherShop.redeem')}
					</BtnLine>
				</Reveal>
			</VsHeroCard>
		</VsHero>
	);
};

const AddCircle = ({
	line,
	label,
	name,
}: {
	line: GiftLine;
	label: string;
	name: string;
}) => {
	const { addGift, countOf } = useGiftBasket();
	const [busy, setBusy] = useState(false);
	const count = countOf(line);

	return (
		<QuickAddBtn
			type='button'
			className={count ? 'vs-on' : ''}
			aria-label={label}
			aria-busy={busy}
			onClick={async (e) => {
				e.stopPropagation();
				if (busy) return;
				setBusy(true);
				await addGift(line, name);
				setBusy(false);
			}}
		>
			{count ? <IcCheck size={18} /> : <IcBasket size={19} />}
			{count > 1 && <i className='vs-qn'>{count}</i>}
		</QuickAddBtn>
	);
};

/** The live /premade sale ticket: tap opens the details popup, the corner
 *  circle adds one more gift copy. */
const GiftTicket = ({
	quiz,
	onOpen,
}: {
	quiz: IPremadeQuiz;
	onOpen: (quiz: IPremadeQuiz) => void;
}) => {
	const { t } = useTranslation();
	const levelKey = premadeLevelKey(quiz.level);

	return (
		<SaleCard
			role='button'
			tabIndex={0}
			data-id='voucher-ticket'
			onClick={() => onOpen(quiz)}
			onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
				if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(quiz);
			}}
		>
			<div className='ph'>
				<img
					className='bg'
					src={assetUrl(quiz.cover_photo_small)}
					alt={quiz.title}
					loading='lazy'
					data-darkzone
				/>
				<AddCircle
					line={{ kind: 'premade', uuid: quiz.uuid }}
					label={t('premade.single.addToCart')}
					name={quiz.title}
				/>
			</div>
			<div className='tt'>
				<h3>{quiz.title}</h3>
				<span className='im'>
					{levelKey && `${t(levelKey)} · `}
					{t('premade.card.questionCount', {
						count: quiz.number_of_questions - 1,
					})}
				</span>
			</div>
			<div className='holes' aria-hidden='true' />
			<div className='stub'>
				<div className='pr'>
					<span className='lb'>{t('premade.card.priceLabel')}</span>
					<span className='eur'>{cardPrice(quiz.price)} EUR</span>
				</div>
				<span className='by'>
					<CardAuthorMark displayName={quiz.author?.display_name} />
				</span>
			</div>
		</SaleCard>
	);
};

const FeaturedCover = ({
	quiz,
	phone,
	onOpen,
}: {
	quiz: IPremadeQuiz;
	phone: boolean;
	onOpen: (quiz: IPremadeQuiz) => void;
}) => {
	const photoRef = useRef<HTMLSpanElement>(null);

	// same gentle drift as the premade homepage's featured cover; phone covers
	// are small, so they get less travel to stay inside the overscan
	useParallax(photoRef, phone ? 0.012 : 0.03);

	return (
		<VsCover type='button' onClick={() => onOpen(quiz)}>
			<span className='ph' data-darkzone>
				<span className='drift' ref={photoRef}>
					<img src={assetUrl(quiz.cover_photo_small)} alt='' />
				</span>
				<b>{quiz.title}</b>
			</span>
		</VsCover>
	);
};

const QuizRail = ({
	title,
	quizzes,
	onOpen,
	onMore,
}: {
	title: string;
	quizzes: IPremadeQuiz[];
	onOpen: (quiz: IPremadeQuiz) => void;
	onMore: () => void;
}) => {
	const { t } = useTranslation();
	const railRef = useRef<HTMLDivElement>(null);
	const [atStart, setAtStart] = useState(true);
	const [atEnd, setAtEnd] = useState(false);

	const sync = useCallback(() => {
		const r = railRef.current;
		if (!r) return;
		setAtStart(r.scrollLeft < 4);
		setAtEnd(r.scrollLeft + r.clientWidth > r.scrollWidth - 4);
	}, []);

	useEffect(sync, [sync, quizzes.length]);

	const nudge = (dir: number) =>
		railRef.current?.scrollBy({
			left: dir * railRef.current.clientWidth * 0.8,
			behavior: 'smooth',
		});

	if (!quizzes.length) return null;

	return (
		<VsSection>
			<Reveal className='head'>
				<h2>{title}</h2>
				<button
					type='button'
					className='all'
					aria-label={t('voucherShop.seeAll')}
					onClick={onMore}
				>
					<span>{t('voucherShop.seeAll')}</span>
					<IcArrowRight size={18} />
				</button>
			</Reveal>
			<Reveal as={VsRail} delay={1}>
				{!atStart && (
					<button
						type='button'
						className='arrow l'
						aria-label={t('voucherShop.back')}
						onClick={() => nudge(-1)}
					>
						<IcArrowRight size={17} />
					</button>
				)}
				<div className='track' ref={railRef} onScroll={sync}>
					{quizzes.map((quiz) => (
						<GiftTicket key={quiz.uuid} quiz={quiz} onOpen={onOpen} />
					))}
					<VsEndCard type='button' onClick={onMore}>
						<IcArrowRight size={26} />
						<i>{t('voucherShop.seeAll')}</i>
					</VsEndCard>
				</div>
				{!atEnd && (
					<button
						type='button'
						className='arrow r'
						aria-label={t('voucherShop.next')}
						onClick={() => nudge(1)}
					>
						<IcArrowRight size={17} />
					</button>
				)}
			</Reveal>
		</VsSection>
	);
};

/** The question pool as dots: a fixed share the recipient already has, the
 *  picked number reshuffles on every tap and every copy in the cart. An
 *  illustration, not the real pool. */
const specialNumber = (title: string) => +(title.match(/#(\d+)/)?.[1] ?? 0);

const POOL_RIPPLE_MS = 260;
const POOL_DRAW_MS = 380;
const POOL_DRAW_MAX_STEP_MS = 14;

const QuestionPool = ({
	questions,
	seed,
	copies,
	phone,
}: {
	questions: number;
	seed: number;
	copies: number;
	phone: boolean;
}) => {
	const { t } = useTranslation();

	const dots = useMemo(() => {
		const columns = phone ? 26 : 48;
		const total = phone ? 234 : 384;
		const rows = Math.ceil(total / columns);
		const base = seeded(17);
		const kinds: string[] = Array.from({ length: total }, () =>
			base() < 0.17 ? 'seen' : ''
		);
		const free = kinds.flatMap((kind, i) => (kind ? [] : [i]));
		const shuffle = seeded(seed + questions * 31 + copies * 977);
		for (let i = free.length - 1; i > 0; i--) {
			const j = Math.floor(shuffle() * (i + 1));
			[free[i], free[j]] = [free[j], free[i]];
		}
		// the draw starts once the ripple has reached the edges, and takes about
		// the same time whichever number was picked
		const drawStep = Math.min(POOL_DRAW_MAX_STEP_MS, POOL_DRAW_MS / questions);
		const drawn = new Map(free.slice(0, questions).map((dot, i) => [dot, i]));
		const farthest = Math.hypot(columns / 2, rows / 2);

		return kinds.map((kind, i) => {
			const order = drawn.get(i);
			const ripple =
				(Math.hypot(
					(i % columns) - columns / 2,
					Math.floor(i / columns) - rows / 2
				) /
					farthest) *
				POOL_RIPPLE_MS;
			return {
				kind: order === undefined ? kind : 'on',
				style: {
					'--ripple': `${Math.round(ripple)}ms`,
					'--draw': `${Math.round(POOL_RIPPLE_MS + (order ?? 0) * drawStep)}ms`,
				} as CSSProperties,
			};
		});
	}, [copies, phone, questions, seed]);

	return (
		<VsPool>
			{/* remounted on every new draw so the animation plays again */}
			<div
				key={`${questions}-${seed}-${copies}`}
				className='dots'
				role='img'
				aria-label={t('voucherShop.quick.poolLabel', { count: questions })}
			>
				{dots.map(({ kind, style }, i) => (
					<i key={i} className={kind} style={style} />
				))}
			</div>
			<div className='legend'>
				<span>
					<i />
					{t('voucherShop.quick.legendPool')}
				</span>
				<span>
					<i className='seen' />
					{t('voucherShop.quick.legendSeen')}
				</span>
				<span>
					<i className='on' />
					<b>{questions}</b> {t('voucherShop.quick.legendPicked')}
				</span>
			</div>
		</VsPool>
	);
};

const QuickBand = ({ phone }: { phone: boolean }) => {
	const { t } = useTranslation();
	const { addGift, countOf } = useGiftBasket();
	const [questions, setQuestions] = useState(30);
	const [seed, setSeed] = useState(5);
	const [busy, setBusy] = useState(false);
	const line: GiftLine = { kind: 'quick', questions };
	const copies = countOf(line);

	return (
		<VsBand id='gift-opce'>
			<Reveal className='lead'>
				<h2>
					{t('voucherShop.quick.title')}{' '}
					<em>{t('voucherShop.quick.accent')}</em>
				</h2>
				<span className='hand'>{t('voucherShop.quick.hand')}</span>
			</Reveal>
			<Reveal as='p' className='txt' delay={1}>
				{t('voucherShop.quick.text')}
			</Reveal>
			<Reveal className='picker' delay={1}>
				<div className='row'>
					<div className='nums'>
						{QUICK_GIFT_OPTIONS.map((n) => (
							<button
								key={n}
								type='button'
								className={n === questions ? 'on' : ''}
								aria-pressed={n === questions}
								onClick={() => {
									setQuestions(n);
									setSeed(Math.floor(Math.random() * 100000));
								}}
							>
								<span className='n'>{n}</span>
							</button>
						))}
					</div>
					<BtnTeal
						type='button'
						className='add'
						aria-busy={busy}
						onClick={async () => {
							if (busy) return;
							setBusy(true);
							await addGift(line, t('cart.quizType.quick', { num: questions }));
							setBusy(false);
						}}
					>
						<IcBasket size={19} />
						<span>
							{copies
								? t('voucherShop.addAnother')
								: t('premade.single.addToCart')}{' '}
							· <span className='cur'>{QUICK_GIFT_PRICES[questions]} EUR</span>
						</span>
					</BtnTeal>
				</div>
				<Reveal className='pool'>
					<QuestionPool
						questions={questions}
						seed={seed}
						copies={copies}
						phone={phone}
					/>
				</Reveal>
			</Reveal>
		</VsBand>
	);
};

const FaqSection = () => {
	const { t } = useTranslation();
	const [open, setOpen] = useState<number | null>(null);

	return (
		<VsFaq id='gift-faq'>
			<Reveal className='hd'>
				<span className='hand'>{t('instructions.kick')}</span>
				<h2>
					{t('voucherShop.faq.title')} <em>{t('voucherShop.faq.accent')}</em>
				</h2>
			</Reveal>
			<Reveal delay={1}>
				{Array.from({ length: FAQ_COUNT }, (_, i) => i + 1).map((n) => (
					<FaqQRow
						key={n}
						className={open === n ? 'open' : ''}
						onClick={() => setOpen(open === n ? null : n)}
					>
						<div className='q'>
							<h3>{t(`voucherShop.faq.q${n}`)}</h3>
							<span className='tgl' aria-hidden='true' />
						</div>
						<AnimatePresence initial={false}>
							{open === n && (
								<motion.div
									onClick={(e) => e.stopPropagation()}
									key='answer'
									initial={{ height: 0, opacity: 0 }}
									animate={{ height: 'auto', opacity: 1 }}
									exit={{ height: 0, opacity: 0 }}
									transition={{ duration: 0.3, ease: 'easeInOut' }}
									style={{ overflow: 'hidden', cursor: 'auto' }}
								>
									<div className='a'>
										<p>
											<Trans
												i18nKey={`voucherShop.faq.a${n}`}
												components={{
													lnk: <Link to={RoutePath.VoucherRedeem} />,
												}}
											/>
										</p>
									</div>
								</motion.div>
							)}
						</AnimatePresence>
					</FaqQRow>
				))}
			</Reveal>
		</VsFaq>
	);
};

const SWIPE_CLOSE_PX = 110;
const SWIPE_CLOSE_SPEED = 0.7; // px per ms

/**
 * The details sheet on touch screens. Its content scrolls, so a swipe that
 * starts while scrolled down only scrolls, even once it reaches the top; the
 * next swipe down, from the top, moves the sheet and closes it. The handle
 * above the content always moves the sheet.
 */
const useSheetSwipe = (
	panelRef: RefObject<HTMLDivElement>,
	enabled: boolean,
	onClose: () => void
) => {
	const closeRef = useRef(onClose);
	closeRef.current = onClose;

	useEffect(() => {
		const panel = panelRef.current;
		if (!enabled || !panel) return;
		const scroller = panel.querySelector('.body');
		let startX = 0;
		let startY = 0;
		let lastY = 0;
		let lastTime = 0;
		let speed = 0;
		let canMove = false;
		let decided = false;
		let moving = false;

		const onStart = (e: TouchEvent) => {
			canMove = false;
			moving = false;
			decided = false;
			if (e.touches.length !== 1) return;
			const inside = scroller?.contains(e.target as Node);
			canMove = !inside || (scroller?.scrollTop ?? 0) <= 0;
			startX = e.touches[0].clientX;
			startY = lastY = e.touches[0].clientY;
			lastTime = e.timeStamp;
			speed = 0;
		};

		const onMove = (e: TouchEvent) => {
			if (!canMove) return;
			const y = e.touches[0].clientY;
			const dy = y - startY;
			const dx = e.touches[0].clientX - startX;
			if (!decided) {
				if (Math.abs(dy) < 4 && Math.abs(dx) < 4) return;
				decided = true;
				moving = dy > 0 && dy > Math.abs(dx);
				if (!moving) {
					canMove = false;
					return;
				}
				panel.style.transition = 'none';
			}
			if (e.cancelable) e.preventDefault();
			speed = (y - lastY) / Math.max(1, e.timeStamp - lastTime);
			lastY = y;
			lastTime = e.timeStamp;
			panel.style.transform = `translateY(${Math.max(0, dy)}px)`;
		};

		const settle = (mayClose: boolean) => {
			if (!moving) return;
			moving = false;
			panel.style.transition = 'transform 0.25s ease';
			const moved = lastY - startY;
			if (mayClose && (moved > SWIPE_CLOSE_PX || speed > SWIPE_CLOSE_SPEED)) {
				closeRef.current();
			} else {
				panel.style.transform = '';
			}
		};
		const onEnd = () => settle(true);
		const onCancel = () => settle(false);

		// not passive: the sheet has to stop the page from scrolling along
		panel.addEventListener('touchstart', onStart, { passive: true });
		panel.addEventListener('touchmove', onMove, { passive: false });
		panel.addEventListener('touchend', onEnd);
		panel.addEventListener('touchcancel', onCancel);
		return () => {
			panel.removeEventListener('touchstart', onStart);
			panel.removeEventListener('touchmove', onMove);
			panel.removeEventListener('touchend', onEnd);
			panel.removeEventListener('touchcancel', onCancel);
		};
	}, [enabled, panelRef]);
};

const QuizPopup = ({
	quiz,
	onClose,
}: {
	quiz: IPremadeQuiz;
	onClose: () => void;
}) => {
	const { t } = useTranslation();
	const { addGift, countOf } = useGiftBasket();
	const [busy, setBusy] = useState(false);
	const line: GiftLine = { kind: 'premade', uuid: quiz.uuid };
	const count = countOf(line);
	const level = quiz.level ?? 0;
	const levelKey = premadeLevelKey(level);
	const panelRef = useRef<HTMLDivElement>(null);
	const { touchDevice } = useWindowListener();
	useSheetSwipe(panelRef, touchDevice, onClose);

	return (
		<Modal closeModal={onClose} explanation ownSwipe>
			<VsQuizPanel ref={panelRef}>
				<span className='m-handle' aria-hidden='true' />
				<ModalCloseBtn
					type='button'
					aria-label={t('common.close')}
					onClick={onClose}
				>
					<IcClose size={15} />
				</ModalCloseBtn>
				<div className='body'>
					<div className='qh'>
						<h1>{quiz.title}</h1>
						{quiz.short_description && (
							<span className='hand'>{quiz.short_description}</span>
						)}
					</div>
					<div className='qm' data-darkzone>
						<img src={assetUrl(quiz.cover_photo)} alt={quiz.title} />
						<span className='shade' />
					</div>
					<SpecBar className='spec'>
						<div className='cell'>
							<span className='k'>{t('premade.single.author')}</span>
							<span className='v hand'>
								<AuthorMark
									displayName={quiz.author?.display_name}
									linked={false}
								/>
							</span>
						</div>
						<div className='cell'>
							<span className='k'>{t('premade.single.questionCount')}</span>
							<span className='v'>{quiz.number_of_questions - 1}</span>
						</div>
						{level > 0 && (
							<div className='cell flex'>
								<span className='k'>{t('premade.single.difficulty')}</span>
								<span className='v'>
									{levelKey && t(levelKey)}
									<span className='lvl'>
										<i className={`l1${level === 1 ? ' f' : ''}`} />
										<i className={`l2${level === 2 ? ' f' : ''}`} />
										<i className={`l3${level === 3 ? ' f' : ''}`} />
									</span>
								</span>
							</div>
						)}
						{quiz.price && (
							<div className='cell'>
								<span className='k'>{t('premade.single.price')}</span>
								<span className='v price'>
									{quiz.price.replace('.', ',')} EUR
								</span>
							</div>
						)}
					</SpecBar>
					<div className='qb'>
						{quiz.long_description && <p>{quiz.long_description}</p>}
						<BtnTeal
							type='button'
							aria-busy={busy}
							onClick={async () => {
								if (busy) return;
								setBusy(true);
								await addGift(line, quiz.title);
								setBusy(false);
							}}
						>
							<IcBasket size={19} />
							{count
								? t('voucherShop.addAnotherCount', { count })
								: t('premade.single.addToCart')}
						</BtnTeal>
					</div>
					{quiz.answers_template && (
						<button
							type='button'
							className='tpl'
							onClick={() => handleDownloadPremadeTemplate(quiz)}
						>
							<IcDoc size={22} />
							{t('premade.single.printableTemplate')}
						</button>
					)}
				</div>
			</VsQuizPanel>
		</Modal>
	);
};

const FilterPopup = ({
	scope,
	filters,
	setFilters,
	onClose,
}: {
	scope: IPremadeQuiz[];
	filters: Filters;
	setFilters: (filters: Filters) => void;
	onClose: () => void;
}) => {
	const { t } = useTranslation();
	const authors = useMemo(
		() => [
			...new Set(scope.map((q) => q.author?.display_name).filter(Boolean)),
		],
		[scope]
	) as string[];
	const shown = scope.filter((q) => matches(q, filters)).length;
	const active = filters.levels.length > 0 || filters.authors.length > 0;

	return (
		<ModalShell
			onClose={onClose}
			title={t('voucherShop.filter.title')}
			actions={
				<>
					{active && (
						<BtnLine
							type='button'
							$small
							onClick={() => setFilters(NO_FILTERS)}
						>
							{t('voucherShop.filter.clear')}
						</BtnLine>
					)}
					<BtnTeal type='button' $small disabled={!shown} onClick={onClose}>
						{t('voucherShop.filter.show', { count: shown })}
					</BtnTeal>
				</>
			}
		>
			<VsFilterChips>
				<span className='lab'>{t('premade.single.difficulty')}</span>
				<div className='chips'>
					{[1, 2, 3].map((level) => (
						<KChip
							key={level}
							type='button'
							$on={filters.levels.includes(level)}
							$tone={LEVEL_TONES[level]}
							onClick={() =>
								setFilters({
									...filters,
									levels: toggle(filters.levels, level),
								})
							}
						>
							{t(premadeLevelKey(level) ?? '')}
						</KChip>
					))}
				</div>
				<span className='lab'>{t('premade.single.author')}</span>
				<div className='chips'>
					{authors.map((author) => (
						<button
							key={author}
							type='button'
							className={
								filters.authors.includes(author) ? 'author on' : 'author'
							}
							aria-pressed={filters.authors.includes(author)}
							onClick={() =>
								setFilters({
									...filters,
									authors: toggle(filters.authors, author),
								})
							}
						>
							<CardAuthorMark displayName={author} />
						</button>
					))}
				</div>
			</VsFilterChips>
		</ModalShell>
	);
};

function toggle<T>(list: T[], value: T) {
	return list.includes(value)
		? list.filter((x) => x !== value)
		: [...list, value];
}

function matches(quiz: IPremadeQuiz, { levels, authors }: Filters) {
	return (
		(!levels.length || levels.includes(quiz.level ?? 0)) &&
		(!authors.length || authors.includes(quiz.author?.display_name ?? ''))
	);
}

const ActiveFilters = ({
	filters,
	setFilters,
}: {
	filters: Filters;
	setFilters: (filters: Filters) => void;
}) => {
	const { t } = useTranslation();
	if (!filters.levels.length && !filters.authors.length) return null;

	return (
		<VsFilterChips className='active'>
			<div className='chips'>
				{filters.levels.map((level) => (
					<KChip
						key={level}
						type='button'
						className='removable'
						$on
						$tone={LEVEL_TONES[level]}
						onClick={() =>
							setFilters({
								...filters,
								levels: toggle(filters.levels, level),
							})
						}
					>
						{t(premadeLevelKey(level) ?? '')} <IcClose size={11} />
					</KChip>
				))}
				{filters.authors.map((author) => (
					<button
						key={author}
						type='button'
						className='author on'
						onClick={() =>
							setFilters({
								...filters,
								authors: toggle(filters.authors, author),
							})
						}
					>
						<CardAuthorMark displayName={author} /> <IcClose size={11} />
					</button>
				))}
			</div>
		</VsFilterChips>
	);
};

const FilterButton = ({
	count,
	onClick,
}: {
	count: number;
	onClick: () => void;
}) => {
	const { t } = useTranslation();

	return (
		<VsFilterOpen type='button' onClick={onClick}>
			{t('voucherShop.filter.label')}
			<span className='ic'>
				<SlidersIcon />
				{count > 0 && <i className='vs-qn'>{count}</i>}
			</span>
		</VsFilterOpen>
	);
};

/**
 * Gift shop at /gift, built after the PlayStation Store: landing, tabs under
 * it, six specials, theme rails, kviz općeg znanja with the question pool,
 * theme tiles and FAQ. Logged-out visitors fill a basket in the browser and
 * sign in when they head for the cart. Entering a code lives at /gift/redeem.
 */
export const VoucherShop = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { width } = useWindowListener();
	const phone = width < PHONE;
	const { premadeQuizzes, loading, error, getPremadeQuizzes } =
		usePremadeQuizzes();
	const { refreshGiftCounts } = useGiftBasket();
	const [params, setParams] = useSearchParams();
	const [openQuiz, setOpenQuiz] = useState<IPremadeQuiz | null>(null);
	const [filterOpen, setFilterOpen] = useState(false);
	const [filters, setFilters] = useState<Filters>(NO_FILTERS);
	const tabsRef = useRef<HTMLDivElement>(null);

	const tabParam = params.get('tab') as GiftTabKey | null;
	const shelf = params.get('tema') as ShelfKey | null;
	const allQuizzes = tabParam === 'svi';
	const landing = !allQuizzes && !shelf;

	// the landing stays mounted while a theme or "svi kvizovi" is open, so going
	// back finds it as it was; it first mounts when it's first needed
	const landingSeen = useRef(landing);
	if (landing) landingSeen.current = true;

	// the section in view; while a click scrolls there, passing sections don't count
	const [section, setSection] = useState<GiftTabKey | null>('izd');
	const scrollingTo = useRef<number | null>(null);
	// a section asked for before it could be scrolled to
	const pendingSection = useRef<GiftTabKey | null>(
		tabParam === 'opce' || tabParam === 'teme' ? tabParam : null
	);
	// where the landing was when a theme opened
	const returnY = useRef<number | null>(null);

	const updateSection = useCallback(() => {
		if (scrollingTo.current !== null) return;
		const bar = tabsRef.current?.getBoundingClientRect().bottom ?? 0;
		const passed = (id: string) => {
			const el = document.getElementById(id);
			return !!el && el.getBoundingClientRect().top <= bar + 60;
		};
		if (passed('gift-faq')) setSection(null);
		else if (passed('gift-teme')) setSection('teme');
		else if (passed('gift-opce')) setSection('opce');
		else setSection('izd');
	}, []);

	const scrollToSection = useCallback((key: GiftTabKey) => {
		const el = document.getElementById(`gift-${key}`);
		const bar = tabsRef.current;
		if (!el || !bar) return;
		// a block still waiting for its reveal sits 34px lower than its place
		const waiting = el.classList.contains('rv') && !el.classList.contains('in');
		const top =
			el.getBoundingClientRect().top + window.scrollY - (waiting ? 34 : 0);
		// scrolling up brings the nav bar back above the sticky menu
		const nav =
			top < window.scrollY && window.innerWidth > SHELL_BREAK_PX
				? NAV_BAR_PX
				: 0;
		const gap = key === 'opce' ? 0 : SECTION_GAP_PX;
		window.scrollTo({
			top: top - bar.offsetHeight - nav - gap,
			behavior: 'smooth',
		});
		setSection(key);
		const settle = () => {
			if (scrollingTo.current !== null) {
				window.clearTimeout(scrollingTo.current);
			}
			scrollingTo.current = window.setTimeout(() => {
				scrollingTo.current = null;
				window.removeEventListener('scroll', settle);
			}, 160);
		};
		window.addEventListener('scroll', settle, { passive: true });
		settle();
	}, []);

	useEffect(() => {
		if (!landing) return;
		window.addEventListener('scroll', updateSection, { passive: true });
		updateSection();
		return () => window.removeEventListener('scroll', updateSection);
	}, [landing, updateSection, premadeQuizzes]);

	// back from a theme: to the same spot, without the page's smooth scrolling
	useLayoutEffect(() => {
		if (shelf || returnY.current === null) return;
		window.scrollTo({ top: returnY.current, behavior: 'instant' });
		returnY.current = null;
	}, [shelf]);

	useEffect(() => {
		// the rows above a section appear with the catalogue and move it down
		if (!landing || !pendingSection.current || !(premadeQuizzes || error)) {
			return;
		}
		const key = pendingSection.current;
		pendingSection.current = null;
		scrollToSection(key);
	}, [landing, premadeQuizzes, error, scrollToSection]);

	useEffect(() => {
		getPremadeQuizzes();
		void refreshGiftCounts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const shelves = useMemo(() => {
		const all = premadeQuizzes ?? [];
		const ordered = (list: IPremadeQuiz[]) => [
			...list.filter((q) => q.section === 'hot').sort(sortBySectionOrder),
			...sortQuizzesNewestFirst(list.filter((q) => q.section !== 'hot')),
		];
		const spec = all
			.filter((q) => /specijal/i.test(q.title ?? ''))
			.sort((a, b) => {
				const maxi = +/maxi/i.test(a.title) - +/maxi/i.test(b.title);
				return maxi || specialNumber(a.title) - specialNumber(b.title);
			});
		const specSet = new Set(spec.map((q) => q.uuid));
		const themed = (key: ShelfKey) =>
			ordered(all.filter((q) => hasTag(q, THEME_TAGS[key] ?? '')));
		const taken = new Set(
			Object.values(THEME_TAGS).flatMap((tag) =>
				all.filter((q) => hasTag(q, tag)).map((q) => q.uuid)
			)
		);

		return {
			top: all.filter((q) => q.section === 'hot').sort(sortBySectionOrder),
			film: themed('film'),
			sport: themed('sport'),
			put: themed('put'),
			glazba: themed('glazba'),
			poveznica: themed('poveznica'),
			ostalo: sortQuizzesNewestFirst(
				all.filter((q) => !taken.has(q.uuid) && !specSet.has(q.uuid))
			),
			djeca: all.filter((q) => q.for_kids),
			spec,
			sve: sortQuizzesNewestFirst(all),
		} as Record<ShelfKey, IPremadeQuiz[]>;
	}, [premadeQuizzes]);

	const featured = shelves.spec
		.filter((q) => !/maxi/i.test(q.title))
		.slice(0, FEATURED_SIZE);

	// "svi kvizovi" is its own view; the others are sections of the landing
	const goTab = (next: GiftTabKey) => {
		setFilters(NO_FILTERS);
		if (next === 'svi') {
			setParams({ tab: 'svi' });
			window.scrollTo({ top: 0, behavior: 'instant' });
			return;
		}
		const nextParams: Record<string, string> =
			next === 'izd' ? {} : { tab: next };
		if (allQuizzes) {
			pendingSection.current = next;
			setParams(nextParams);
		} else {
			setParams(nextParams, { replace: true });
			scrollToSection(next);
		}
	};

	const openShelf = (key: ShelfKey) => {
		returnY.current = window.scrollY;
		setParams({ tema: key });
		window.scrollTo({ top: 0, behavior: 'instant' });
	};

	const closeShelf = () => {
		// a real step back when we came from the landing, so its URL returns too
		if (returnY.current !== null) navigate(-1);
		else setParams({});
	};

	const redeem = () => navigate(RoutePath.VoucherRedeem);

	const tiles = (
		<Reveal as={VsThemes}>
			{THEME_TILES.map(({ key, icon }) => (
				<button key={key} type='button' onClick={() => openShelf(key)}>
					{icon}
					<span>{t(`voucherShop.shelf.${key}`)}</span>
				</button>
			))}
		</Reveal>
	);

	const grid = (quizzes: IPremadeQuiz[]) =>
		quizzes.length ? (
			<Reveal as={VsGrid}>
				{quizzes.map((quiz) => (
					<GiftTicket key={quiz.uuid} quiz={quiz} onOpen={setOpenQuiz} />
				))}
			</Reveal>
		) : (
			!loading && <VsNone>{t('voucherShop.filter.none')}</VsNone>
		);

	const filterCount = filters.levels.length + filters.authors.length;
	const scope = shelves.sve;

	const izdvojeno = (
		<>
			<Reveal as={VsTopGrid} id='gift-izd'>
				{loading && !premadeQuizzes
					? Array.from({ length: FEATURED_SIZE }, (_, i) => (
							<Sk key={i} style={{ aspectRatio: '3 / 2', borderRadius: 18 }} />
						))
					: featured.map((quiz) => (
							<FeaturedCover
								key={quiz.uuid}
								quiz={quiz}
								phone={phone}
								onOpen={setOpenQuiz}
							/>
						))}
			</Reveal>
			<QuizRail
				title={t('voucherShop.shelf.film')}
				quizzes={shelves.film.slice(0, RAIL_SIZE)}
				onOpen={setOpenQuiz}
				onMore={() => openShelf('film')}
			/>
			<QuizRail
				title={t('voucherShop.shelf.put')}
				quizzes={shelves.put}
				onOpen={setOpenQuiz}
				onMore={() => openShelf('put')}
			/>
			<QuizRail
				title={t('voucherShop.shelf.sport')}
				quizzes={shelves.sport.slice(0, RAIL_SIZE)}
				onOpen={setOpenQuiz}
				onMore={() => openShelf('sport')}
			/>
			<QuickBand phone={phone} />
			<VsSection className='pad' id='gift-teme'>
				{tiles}
			</VsSection>
			<FaqSection />
		</>
	);

	return (
		<VsPage data-id='voucher-shop'>
			<Navigation />

			{error && (
				<div style={{ padding: '120px var(--pad) 0' }}>
					<AlertMessage code={ApiFailedReason.default} type='error' />
				</div>
			)}

			{shelf && (
				<VsSection className='pad shelf'>
					<Reveal as={VsShelfHead}>
						<button
							type='button'
							className='back'
							aria-label={t('voucherShop.back')}
							onClick={closeShelf}
						>
							<IcArrowRight size={17} />
						</button>
						<h1>{t(`voucherShop.shelf.${shelf}`)}</h1>
					</Reveal>
					{grid(shelves[shelf] ?? [])}
				</VsSection>
			)}

			<div style={shelf ? { display: 'none' } : undefined}>
				{landingSeen.current && (
					<div style={allQuizzes ? { display: 'none' } : undefined}>
						<Hero
							onGift={() => scrollToSection('izd')}
							onRedeem={redeem}
							phone={phone}
						/>
					</div>
				)}
				{allQuizzes && <div className='top-gap' />}
				<GiftTabs
					barRef={tabsRef}
					active={allQuizzes ? 'svi' : section}
					flush={allQuizzes}
					onSelect={goTab}
					onRedeem={redeem}
				/>
				{allQuizzes && (
					<VsSection className='pad'>
						<div className='head flat'>
							<ActiveFilters filters={filters} setFilters={setFilters} />
							<FilterButton
								count={filterCount}
								onClick={() => setFilterOpen(true)}
							/>
						</div>
						{grid(scope.filter((q) => matches(q, filters)))}
					</VsSection>
				)}
				{landingSeen.current && (
					<div
						className='sections'
						style={allQuizzes ? { display: 'none' } : undefined}
					>
						{izdvojeno}
					</div>
				)}
			</div>

			{openQuiz && (
				<QuizPopup quiz={openQuiz} onClose={() => setOpenQuiz(null)} />
			)}
			{filterOpen && allQuizzes && !shelf && (
				<FilterPopup
					scope={scope}
					filters={filters}
					setFilters={setFilters}
					onClose={() => setFilterOpen(false)}
				/>
			)}
		</VsPage>
	);
};
